<template>
  <ValidationObserver
    ref="editNumberObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="edit-number-modal"
      ref="addNewMemberModal"
      modal-class="settings-modal no-multiselect-overflow"
      :body-class="['body-wrapper', 'white-bg']"
      :title="`Edit ${number.name}`"
      hide-header-close
      size="md"
      centered
      scrollable
      @hidden="clear">
      <b-form-group class="no-margin-bottom">
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          class="greedy"
          name="Name" >
          <label class="settings__label">Number name</label>
          <div class="number-info flex relative">
            <span
              v-if="newEmojiIcon || !numberIntegrationMap[number.id] || !numberIntegrationMap[number.id].length"
              class="branch-icon flex center-all emoji-fix show-hand"
              @click="toggleEmoji" >{{ newEmojiIcon ? newEmojiIcon : number.emoji }}</span>
            <span
              v-else-if="numberIntegrationMap[number.id] && numberIntegrationMap[number.id].length === 1"
              id="branch-icon-fixed"
              :class="['branch-icon', numberIntegrationMap[number.id][0]]" />
            <span
              v-else
              id="branch-icon-fixed"
              class="branch-icon count" >{{ numberIntegrationMap[number.id].length }}</span>
            <input
              v-model="numberName"
              type="text"
              class="w-100"
              placeholder="Enter number name" >

            <picker
              v-show="showEmojiPicker"
              v-click-outside="closeEmojis"
              class="emoji-picker"
              set="emojione"
              title="Pick your emoji..."
              emoji="point_up"
              :native="true"
              @select="emojiSelected" />

            <b-popover
              v-if="numberIntegrationMap[number.id]"
              target="branch-icon-fixed"
              boundary="window"
              triggers="hover"
              placement="top"
              custom-class="basic-popover btn-popover-additional-margin">
              Icon can't be edited
            </b-popover>
          </div>
          <div
            v-if="errors[0] && filteredMembers.length !== 0"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </b-form-group>
      <!-- Number checkboxes -->
      <b-form-group>
        <label
          for="text-field"
          class="settings__label flex flex-align-center sticky-top" >
          Members
          <span
            v-if="filteredMembers.length"
            class="dot-prefix flex flex-align-center">{{ filteredMembers.length }}</span>
          <span
            class="greedy checkbox-toggle-wrapper" >
            <span
              class="toggle-all-checkboxes"
              @click="toggleMembers">{{ selectedMembers.length === members.length ? 'Deselect all' : 'Select all' }}</span>
          </span>
        </label>
        <SearchInput
          v-if="showMemberSearch"
          ref="searchInput"
          v-model="searchQuery"
          placeholder="Search members..."
          :no-debounce="true"
          :flat="true"
          :icon-size="12"
          :full-width="true"
          @input="() => {}" />
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          tag="div" >
          <b-form-checkbox-group
            v-model="selectedMembers"
            stacked >
            <div
              v-for="(member, index) in filteredMembers"
              :id="`member-${index}`"
              :key="index">
              <b-form-checkbox
                :disabled="['owner'].includes(member.role) && me.id !== member.id"
                :class="['checkbox-with-description', {disabled: ['owner'].includes(member.role) && me.id !== member.id}]"
                :value="member.id" >
                <SBAvatar
                  class="no-shrink avatar"
                  size="s-button-26"
                  :show-data="true"
                  :show-client="true"
                  :character-count="1"
                  :person="member"
                  :hide-avatar-image="true" />
                <div class="option-info greedy">
                  <h3 :inner-html.prop="member.name | boldMatch(searchQuery)" />
                  <p :inner-html.prop="(member.email ? member.email : '' ) | boldMatch(searchQuery)" />
                </div>
                <span
                  v-if="['owner'].includes(member.role) && me.id !== member.id"
                  class="admin" >Owner</span>
                <!-- v-if="!roles.map(role => role.alias).includes(member.role)" -->
              </b-form-checkbox>
              <b-popover
                v-if="['owner'].includes(member.role) && me.id !== member.id"
                custom-class="basic-popover"
                :target="`member-${index}`"
                triggers="hover"
                placement="top"
                :delay="{ show: 200, hide: 0 } " >
                Owner can't be modified.
              </b-popover>
            </div>
          </b-form-checkbox-group>
          <div
            v-if="errors[0] && filteredMembers.length !== 0"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>At least one number has to be selected.</span>
          </div>
          <div
            v-if="filteredMembers.length === 0"
            class="search-empty flex flex-align-center mt-2">
            <span class="emoji">😐</span>
            <p>We couldn't find anything for <b>{{ searchQuery }}</b></p>
          </div>
        </ValidationProvider>
      </b-form-group>

      <template slot="modal-footer">
        <div class="modal-footer__buttons flex flex-row flex-nowrap">
          <b-button @click="hide">Cancel</b-button>
          <b-button
            variant="primary"
            :disabled="saving || ((!valid || pristine || !formChanged) && !newEmojiIcon)"
            :class="{loading: saving}"
            class="flex settings__actions--save"
            @click="confirm" >
            <span>Save</span>
            <Spinner
              v-if="saving"
              :size="14"
              :thickness="2"
              :color="SBColors.white" />
          </b-button>
        </div>

      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import escapeStringRegexp from 'escape-string-regexp';
import { Picker } from 'emoji-mart-vue';

import roles from '@/helpers/lists/roles';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import Spinner from '@/components/common/Spinner';
import SearchInput from '@/components/business/widgets/SearchInput';
import { getNumberIcon } from '@/helpers/lists/branchIcons';
import ToasterMixin from '@/mixins/Toaster';
import SBAvatar from '@/components/common/Avatar';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'EditNumberModal',
  components: {
    SBIcon,
    Spinner,
    SearchInput,
    SBAvatar,
    Picker,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
  ],
  props: {
    iconIndex: {
      type: Number,
      required: false,
      default: 0,
    },
    members: {
      type: Array,
      required: true,
    },
    number: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      errorMessage: '',
      saving: false,
      emails: [],
      lastEmailSearch: '',
      role: roles[2],
      selectedNumbers: [],
      searchQuery: '',
      editMember: null,
      selectedMembers: [],
      numberName: '',
      initalFormData: {},
      latestFormData: {},
      showEmojiPicker: false,
      preventEmojiClose: false,
      newEmojiIcon: null,
      SBColors,
      SBIconList,
      roles,
    };
  },
  computed: {
    ...mapGetters({
      numberIntegrationMap: 'organisation/numberIntegrationMap',
      me: 'organisation/me',
    }),
    filteredMembers() {
      return this.members.filter(member => RegExp(escapeStringRegexp(`${this.searchQuery}`), 'gi').test(member.name) || RegExp(escapeStringRegexp(`${this.searchQuery}`), 'gi').test(member.phone)).filter(member => !member.ai_assistant);
    },
    showMemberSearch() {
      return this.members.length > 1;
    },
    formChanged() {
      return !this.$lodash.isEqual({ ...this.initialFormData }, { ...this.latestFormData });
    },
  },
  watch: {
    newEmojiIcon() {
      this.processForm();
    },
    numberName() {
      this.processForm();
    },
    selectedMembers() {
      this.processForm();
    },
    members() {
      this.populateForm();
    },
  },
  methods: {
    ...mapActions({
      bulkMembersToNumber: 'merchant/bulkMembersToNumber',
      updateNumberName: 'merchant/updateNumberName',
      updateMembersWithNumber: 'organisation/updateMembersWithNumber',
    }),
    getNumberIcon,
    processForm() {
      this.latestFormData = {
        nameData: {
          name: this.numberName,
          emoji: this.newEmojiIcon ? this.newEmojiIcon : this.number.emoji,
        },
        membersData: {
          user_profile_uid: this.members.filter(member => this.selectedMembers.includes(member.id)).map(member => [member.id, member.role]),
          business_uid: [this.number.id],
        },
      };
    },
    closeEmojis() {
      if (!this.preventEmojiClose) {
        this.showEmojiPicker = false;
      }
    },
    emojiSelected(emoji) {
      this.showEmojiPicker = false;
      this.newEmojiIcon = emoji.native;
    },
    confirm() {
      this.saving = true;
      let nameRequest;
      let membersRequest;
      if (!this.$lodash.isEqual({ ...this.initialFormData.nameData }, { ...this.latestFormData.nameData })) {
        this.updateNumberName({
          numberId: this.latestFormData.membersData.business_uid,
          data: this.latestFormData.nameData,
        })
          .then((response) => {
            if (response.status === 200) {
              nameRequest = true;
            }
          })
          .catch(() => {
            nameRequest = false;
          })
          .finally(() => {
            this.finalizeEdit(nameRequest, membersRequest);
          });
      } else {
        nameRequest = null;
      }
      if (!this.$lodash.isEqual({ ...this.initialFormData.membersData }, { ...this.latestFormData.membersData })) {
        this.bulkMembersToNumber(this.latestFormData.membersData)
          .then((response) => {
            if (response.status === 200) {
              membersRequest = true;
              this.updateMembersWithNumber({
                members: this.latestFormData.membersData.user_profile_uid,
                numberId: this.latestFormData.membersData.business_uid,
              });
            }
          })
          .catch(() => {
            membersRequest = false;
          })
          .finally(() => {
            this.finalizeEdit(nameRequest, membersRequest);
          });
      } else {
        membersRequest = null;
      }
    },
    finalizeEdit(nameRequest, membersRequest) {
      if (
        nameRequest === undefined ||
        membersRequest === undefined
      ) {
        // do nothing ...
        return;
      }

      if (nameRequest === false || membersRequest === false) {
        this.showToaster('Something went wrong. Please try again later.', null, null, 'error');
        this.saving = false;
        return;
      }

      this.showToaster(`${this.latestFormData.nameData.name} has been successfully updated.`, null, null, 'OK');
      this.$emit('edited', this.latestFormData);
      this.hide();
    },
    populateForm() {
      this.$nextTick(() => {
        this.numberName = this.number.name;
        if (this.members.length && Array.isArray(this.members[0].businesses) && !this.isObject(this.members[0].businesses[0])) {
          this.selectedMembers = this.members.filter(member => member.businesses.includes(this.number.id)).map(member => member.id);
        } else {
          this.selectedMembers = this.members.filter(member => member.businesses.map(business => business.uid).includes(this.number.id)).map(member => member.id);
        }
        this.processForm();
        this.initialFormData = { ...this.latestFormData };
        this.$refs.editNumberObserver.reset();
      });
    },
    toggleEmoji() {
      this.showEmojiPicker = !this.showEmojiPicker;
      this.preventEmojiClose = true;
      setTimeout(() => { this.preventEmojiClose = false; }, 100);
    },
    show(member = null) {
      if (member && member.id) {
        this.editMember = member;
        this.populateForm(member);
      }
      this.$refs.addNewMemberModal.show();
    },
    clear() {
      this.saving = false;
      this.editMember = null;
      this.errorMessage = '';
      this.emails = [];
      this.lastEmailSearch = '';
      this.role = roles[2];
      this.selectedNumbers = [];
      this.searchQuery = '';
      this.newEmojiIcon = null;
    },
    hide() {
      this.$refs.addNewMemberModal.hide();
    },
    toggleMembers() {
      if (this.selectedMembers.length === this.members.length) {
        this.selectedMembers = [...this.members.filter(member => (['owner'].includes(member.role) && this.me.id !== member.id) || member.ai_assistant).map(member => member.id)];
      } else {
        this.selectedMembers = [...this.members.map(member => member.id)];
      }
    },
  },
};
</script>


<style lang="scss" scoped>
#edit-number-modal {
  max-height: 76% !important;
  top: 12%;

  .modal-body {
    padding-top: 0 !important;

    & > div:first-child {
      margin-top: 20px;
    }

    .mt_0_important {
      margin-top: 0 !important;
    }
  }

  .number-info {
    margin: 0;
    border-radius: 5px;
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px $greyBorder;
    background-color: $white;

    span.branch-icon {
      padding: 7px 13px 7px 10px;
      font-size: 12px !important;
      transform: translateY(1px);

      &.count {
        padding: 7px 12px 7px 12px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.FRONTAPP,
      &.FRONTPACHA {
        background-image: url('~@/assets/img/SB-Icons/integration-front-color.svg');
        background-size: 14px 14px;
        background-position: center center;
        background-repeat: no-repeat;
        display: block;
        width: 31px;
        height: 31px;
        transform: translateY(0);
      }
    }

    .branch-icon {
      border-right: solid 1px $greyBorder;
    }
  }

  .sticky-top {
    // margin: 15px 0 0 0 !important;
    // padding: 5px 0 !important;
    background-color: #f8fafb !important;
    z-index: 49 !important;
  }

  .flat {
    margin-left: 6px !important;

    .search-wrapper input {
      padding-left: 27px !important;
    }
  }

  .search-empty {
    color: $blackLight;

    .emoji {
      margin-left: 6px !important;
      font-size: 13px !important;
      width: 20px !important;
    }

    p {
      margin-left: 7px;

      b {
        font-weight: 500 !important;
      }
    }
  }

  input[type=text] {
    box-sizing: border-box;
    font-size: 14px;
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
    padding: 7px 7px !important;

    &::placeholder {
      color: $greyPrimary !important;
    }

    &:hover {
      box-shadow: none !important;
    }

    &:focus {
      outline: none !important;
      border: none !important;
      box-shadow: none !important;
    }
  }

  .form-group.no-margin-bottom {
    margin-bottom: 0 !important;
  }
}

.emoji-picker {
  position: absolute;
  top: 37px;
  left: -1px;
  z-index: 1000;

  ::v-deep .emoji {
    border: none !important;
  }
}

.disabled {
  ::after, ::before {
    display: none;
  }

  .admin {
    font-size: 11px !important;
    font-weight: 500;
    color: $white;
    background-color: $greyLight;
    padding: 4px 4px;
    border-radius: 5px;
  }
}
</style>
